import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {RefObject, useRef} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import {omit} from 'util/omitKey';
import {InputChangeEvent, InputFocusEvent} from 'util/Types';

import {Size, SIZES, TextInputType} from './InputConstants';

export interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  className?: string;
  /**
   * Whether the input has a button on the right, as with a DateTimePicker. This removes the border radius on
   * the right-hand side of the input so it can abut the button directly.
   * @default false
   */
  hasButton?: boolean;
  /**
   * Whether the input has an error. This causes the input to display with a red outline.
   * @default false
   */
  hasError?: boolean;
  /**
   * One of {@link Size.SMALL} or {@link Size.MEDIUM}. If passed, renders the input at a specific font size
   * and component height (13/28 for small, 14/36 for medium.)
   */
  size?: Size;
  /**
   * Whether to render a regular text input or a password input
   * @default {@link TextInputType.TEXT}
   */
  type?: TextInputType;
  autoFocus?: boolean;
  /**
   * If true, all the text in the input will be selected when the component mounts.
   * @default false
   */
  initialSelect?: boolean;
  onChange?: (e: InputChangeEvent) => void;
  onBlur?: (e: InputFocusEvent) => void;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  dataTestAttrs?: DataTestAttrs;
  inputRef?: RefObject<HTMLInputElement>;
}

export default function TextInput({
  autoFocus = false,
  hasButton = false,
  hasError = false,
  initialSelect = false,
  type = TextInputType.TEXT,
  ...otherProps
}: TextInputProps): JSX.Element {
  // You can't conditionally use hooks, so we always do a useRef and just don't use it if we're passed a
  // ref in props.
  const internalRef = useRef<HTMLInputElement>(null);
  const ref = otherProps.inputRef ?? internalRef;

  useOnMount(() => {
    if (initialSelect) {
      ref.current!.select();
    }
  });

  return (
    <input
      {...omit(otherProps, 'dataTestAttrs', 'inputRef', 'size')}
      className={classNames(
        otherProps.className,
        'fieldInput-input',
        otherProps.readOnly && css(styles.readOnly),
        otherProps.disabled && css(styles.disabled),
        {
          'fieldInput-input--hasButton': hasButton,
          'fieldInput-input--error': hasError,
          'fieldInput-input--small': otherProps.size === SIZES.SMALL,
          'fieldInput-input--medium': otherProps.size === SIZES.MEDIUM,
        }
      )}
      type={type}
      autoFocus={autoFocus}
      onBlur={otherProps.onBlur}
      maxLength={otherProps.maxLength}
      ref={ref}
      {...getTestProps(otherProps.dataTestAttrs)}
    />
  );
}

const styles = StyleSheet.create({
  disabled: {
    backgroundColor: Color.GRAY_100,
    borderColor: Color.GRAY_100,
    // Set on color and placeholder color so the text is the right color for placeholders and values
    color: Color.GRAY_600,
    '::placeholder': {
      color: Color.GRAY_600,
    },
  },
  readOnly: {
    // This overrides the default CSS styling for readonly tags, which is to add a gray background
    background: Colors.WHITE,
  },
});
